import { useEffect, useState } from "react";
import { Loader2, Mail, TrendingUp, UserPlus, UserMinus, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { adminNewsletterConversionStats } from "@/lib/api";

/**
 * Admin tile showing newsletter subscriber funnel performance:
 *  - Total opted-in subscribers
 *  - 7-day / 30-day new signups
 *  - Top-converting articles (sortable by unique emails)
 *  - Unsubscribe count + rate
 *  - Source breakdown (article CTA vs tool CTA vs inline newsletter)
 */
export default function NewsletterConversionDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        adminNewsletterConversionStats()
            .then((data) => {
                if (mounted) setStats(data);
            })
            .catch((e) => {
                if (mounted) setError(e?.response?.data?.detail || e?.message || "Failed to load");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });
        return () => {
            mounted = false;
        };
    }, []);

    if (loading) {
        return (
            <div className="py-12 text-center text-bl-muted" data-testid="newsletter-conversion-loading">
                <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
                Loading conversion stats…
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-6 bl-card border-red-200 bg-red-50 text-red-800" data-testid="newsletter-conversion-error">
                Couldn't load conversion stats: {error}
            </div>
        );
    }
    if (!stats) return null;

    return (
        <div className="space-y-8" data-testid="newsletter-conversion-dashboard">
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard
                    icon={<Mail className="w-4 h-4" />}
                    label="Total subscribers"
                    value={stats.total_subscribers.toLocaleString()}
                    testid="kpi-total-subscribers"
                />
                <KpiCard
                    icon={<UserPlus className="w-4 h-4 text-bl-primary" />}
                    label="New (last 7 days)"
                    value={`+${stats.new_7d.toLocaleString()}`}
                    testid="kpi-new-7d"
                    accent
                />
                <KpiCard
                    icon={<TrendingUp className="w-4 h-4" />}
                    label="New (last 30 days)"
                    value={`+${stats.new_30d.toLocaleString()}`}
                    testid="kpi-new-30d"
                />
                <KpiCard
                    icon={<UserMinus className="w-4 h-4 text-amber-600" />}
                    label="Unsubscribed"
                    value={`${stats.unsubscribes_total.toLocaleString()}  ·  ${stats.unsubscribe_rate_pct}%`}
                    testid="kpi-unsubscribes"
                    helper={`+${stats.unsubscribes_7d} in the last 7d`}
                />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Top-converting articles */}
                <div className="bl-card p-6" data-testid="top-articles-card">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-lg text-bl">
                            Top converting articles
                        </h3>
                        <span className="text-xs text-bl-muted">By unique signups</span>
                    </div>
                    {stats.top_articles.length === 0 ? (
                        <p className="text-sm text-bl-muted">
                            No article-sourced signups yet. The CTA at the bottom of every
                            <span className="font-medium"> /learn </span>article funnels here
                            once visitors start subscribing.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {stats.top_articles.map((a, idx) => (
                                <li
                                    key={a.source}
                                    className="flex items-center justify-between gap-3 py-2 border-b border-bl-line last:border-0"
                                    data-testid={`top-article-row-${idx}`}
                                >
                                    <Link
                                        to={`/learn/${a.slug}`}
                                        className="text-sm text-bl hover:text-bl-primary truncate flex-1 inline-flex items-center gap-1.5"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <span className="truncate">{a.slug}</span>
                                        <ExternalLink className="w-3 h-3 shrink-0 text-bl-muted" />
                                    </Link>
                                    <div className="font-mono-bl text-xs text-bl-muted shrink-0">
                                        {a.unique_emails} sub{a.unique_emails === 1 ? "" : "s"}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Source breakdown */}
                <div className="bl-card p-6" data-testid="source-breakdown-card">
                    <h3 className="font-display font-semibold text-lg text-bl mb-4">
                        Signup source mix
                    </h3>
                    {stats.sources.length === 0 ? (
                        <p className="text-sm text-bl-muted">No signups yet.</p>
                    ) : (
                        <div className="space-y-3">
                            {stats.sources.map((s) => {
                                const total = stats.sources.reduce((acc, x) => acc + x.subscribers, 0);
                                const pct = total > 0 ? Math.round((s.subscribers / total) * 100) : 0;
                                return (
                                    <div key={s.category} data-testid={`source-row-${s.category}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-bl capitalize">
                                                {s.category.replace(/_/g, " ")}
                                            </span>
                                            <span className="font-mono-bl text-xs text-bl-muted">
                                                {s.subscribers} · {pct}%
                                            </span>
                                        </div>
                                        <div className="w-full h-2 bg-bl-cream rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-bl-primary"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function KpiCard({ icon, label, value, testid, accent, helper }) {
    return (
        <div
            className={
                "bl-card p-5 " +
                (accent ? "border-bl-primary/30 bg-bl-primary/5" : "")
            }
            data-testid={testid}
        >
            <div className="flex items-center gap-2 text-bl-muted text-xs uppercase tracking-wide">
                {icon}
                {label}
            </div>
            <div className="mt-2 font-display font-semibold text-2xl text-bl">
                {value}
            </div>
            {helper && (
                <div className="mt-1 text-xs text-bl-muted">{helper}</div>
            )}
        </div>
    );
}
